import { Fragment, useState } from "react";
import { Transition, Dialog } from "@headlessui/react";
import { RENT_PAYMENT_METHODS, RentPaymentMethod } from "@sharedtypes/RentPaymentMethod";
import useSetRentPaymentMethod from "@hooks/useSetRentPaymentMethod";

const OPTIONS: { value: RentPaymentMethod; label: string; description: string }[] = [
  {
    value: RENT_PAYMENT_METHODS.STANDING_ORDER,
    label: "Standing Order",
    description: "I pay by standing order in the first week of the month.",
  },
  {
    value: RENT_PAYMENT_METHODS.DEDUCT_FROM_TAKINGS,
    label: "Deduct from Takings",
    description:
      "Deduct rent from my previous month's takings and pay the net amount. If takings don't cover rent, issue me an invoice.",
  },
  {
    value: RENT_PAYMENT_METHODS.INVOICE,
    label: "Invoice",
    description:
      "Send me an invoice on the 1st of the month and I will pay it in the first week.",
  },
];

type RentPaymentMethodDialogProps = {
  show: boolean;
  onClose: () => void;
};

const RentPaymentMethodDialog = ({ show, onClose }: RentPaymentMethodDialogProps) => {
  const [selected, setSelected] = useState<RentPaymentMethod | null>(null);
  const mutation = useSetRentPaymentMethod();

  function handleSave() {
    if (selected === null) return;
    mutation.mutate(selected, {
      onSuccess: () => onClose(),
    });
  }

  return (
    <Transition appear show={show} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                  How do you pay your rent?
                </Dialog.Title>
                <p className="mt-2 text-sm text-gray-500">
                  Please select how you would like to pay your monthly rent.
                </p>

                <div className="mt-4 space-y-3">
                  {OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className={`block cursor-pointer rounded-lg border-2 p-4 transition-colors ${
                        selected === option.value
                          ? "border-shell-600 bg-shell-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="rentPaymentMethod"
                          value={option.value}
                          checked={selected === option.value}
                          onChange={() => setSelected(option.value)}
                          className="mt-1 text-shell-600 focus:ring-shell-500"
                        />
                        <div>
                          <div className="font-medium text-gray-900">{option.label}</div>
                          <div className="mt-1 text-sm text-gray-500">{option.description}</div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                    onClick={onClose}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="rounded-lg bg-shell-600 px-4 py-2 text-sm font-medium text-white hover:bg-shell-700 disabled:opacity-50"
                    disabled={selected === null || mutation.isPending}
                    onClick={handleSave}
                  >
                    {mutation.isPending ? "Saving..." : "Save"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default RentPaymentMethodDialog;
